import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatAge, getFreshness } from '../lib/liveData';

/**
 * Bloomberg/quant-terminal style live data strip.
 * Shows 5 key feeds with freshness indicator + last-update timestamp.
 * Subtle pulse on the dot, mono-spaced numbers, dividers between cells.
 */
export default function LiveTickerStrip({ inputs, liveData }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('zh') ? 'zh' : 'en';
  const isSandboxed = liveData?.isSandboxed;
  const isLoading = liveData?.isLoading;

  const tiles = [
    {
      key: 'wti',
      label: t('live.wti'),
      value: inputs.wti_price ? `$${Number(inputs.wti_price).toFixed(2)}` : '—',
      ts: liveData?.timestamps?.wti_price,
      manual: !liveData?.timestamps?.wti_price && !isSandboxed,
    },
    {
      key: 'brent',
      label: t('live.brent'),
      value: inputs.brent_price ? `$${Number(inputs.brent_price).toFixed(2)}` : '—',
      ts: liveData?.timestamps?.brent_price,
      manual: !liveData?.timestamps?.brent_price && !isSandboxed,
    },
    {
      key: 'cny',
      label: t('live.cny'),
      value: inputs.currency_rate ? Number(inputs.currency_rate).toFixed(4) : '—',
      ts: liveData?.timestamps?.currency_rate,
      manual: !liveData?.timestamps?.currency_rate && !isSandboxed,
    },
    {
      key: 'peace',
      label: t('live.polymarket'),
      value: inputs.polymarket_peace != null
        ? `${Math.round(inputs.polymarket_peace)}%`
        : '—',
      ts: liveData?.timestamps?.polymarket_peace,
      manual: !liveData?.timestamps?.polymarket_peace && !isSandboxed,
    },
    {
      key: 'dce',
      label: t('live.dce_m1'),
      value: inputs.dce_futures_m1 ? `¥${Number(inputs.dce_futures_m1).toLocaleString()}` : '—',
      ts: liveData?.timestamps?.dce_futures_m1,
      manual: !liveData?.timestamps?.dce_futures_m1 && !isSandboxed,
    },
  ];

  return (
    <div style={{
      background: 'linear-gradient(135deg, var(--bg2) 0%, var(--bg3) 100%)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      padding: '14px 18px',
      marginBottom: 16,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Subtle scan-line effect for tech feel */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: 2,
        background: 'linear-gradient(90deg, transparent, var(--blue) 20%, var(--green) 50%, var(--blue) 80%, transparent)',
        opacity: 0.6,
      }} />

      {/* Header line */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 12,
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Pulse dot */}
          <div style={{
            width: 6, height: 6, borderRadius: '50%',
            background: isSandboxed ? 'var(--amber)' : isLoading ? 'var(--blue)' : 'var(--green)',
            boxShadow: isSandboxed
              ? '0 0 0 0 color-mix(in srgb, var(--amber) 40%, transparent)'
              : isLoading
                ? '0 0 8px color-mix(in srgb, var(--blue) 70%, transparent)'
                : '0 0 8px color-mix(in srgb, var(--emerald) 70%, transparent)',
            animation: isLoading ? 'pulseBlue 1s ease-in-out infinite' : 'pulseGreen 2.5s ease-in-out infinite',
          }} />
          <span style={{
            fontFamily: 'var(--mono)',
            fontSize: 9,
            fontWeight: 700,
            color: isSandboxed ? 'var(--amber)' : isLoading ? 'var(--blue)' : 'var(--green)',
            letterSpacing: '.16em',
          }}>
            {isSandboxed ? t('live.sandboxed_label') : isLoading ? t('live.refreshing') : t('live.feeds_title')}
          </span>
        </div>
        <div style={{ flex: 1, height: 1, background: 'var(--border)', margin: '0 6px' }} />
        <div style={{
          fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--t3)',
          letterSpacing: '.04em',
        }}>
          {liveData?.lastFetchAt
            ? `${t('live.last_refresh')}: ${formatAge(liveData.lastFetchAt, lang)}`
            : t('live.never')}
        </div>
        {!isSandboxed && (
          <button
            onClick={() => liveData?.refresh?.()}
            disabled={isLoading}
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10, fontWeight: 600,
              padding: '3px 9px',
              borderRadius: 4,
              border: '1px solid var(--border)',
              background: 'var(--bg3)',
              color: 'var(--t1)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.4 : 1,
              letterSpacing: '.04em',
            }}
            title={t('live.auto_refresh_note')}
          >
            ↻
          </button>
        )}
      </div>

      {/* Sandboxed message */}
      {isSandboxed && (
        <div style={{
          fontSize: 11,
          color: 'var(--amber)',
          marginBottom: 10,
          padding: '6px 10px',
          background: 'var(--amber-l)',
          borderRadius: 6,
          border: '1px solid var(--amber-b)',
        }}>
          ℹ {t('live.sandboxed_msg')}
        </div>
      )}

      {/* Tiles row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 0,
        background: 'var(--bg)',
        borderRadius: 8,
        border: '1px solid var(--border)',
        overflow: 'hidden',
      }}>
        {tiles.map((tile, idx) => {
          const freshness = tile.manual ? 'manual' : tile.ts ? getFreshness(tile.ts) : 'missing';
          const dotColor = freshness === 'live'    ? 'var(--green)'
                         : freshness === 'stale'   ? 'var(--amber)'
                         : freshness === 'manual'  ? 'var(--t3)'
                         : 'var(--red)';
          const dotShadow = freshness === 'live' ? '0 0 5px color-mix(in srgb, var(--emerald) 60%, transparent)' : 'none';

          return (
            <div key={tile.key} style={{
              padding: '10px 12px',
              borderRight: idx < tiles.length - 1 ? '1px solid var(--border)' : 'none',
              borderBottom: '1px solid transparent',
              minWidth: 0,
            }} className="ticker-tile">
              {/* Top: label + status dot */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
                <div style={{
                  width: 5, height: 5, borderRadius: '50%',
                  background: dotColor,
                  boxShadow: dotShadow,
                  flexShrink: 0,
                }} />
                <span style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 8,
                  fontWeight: 600,
                  color: 'var(--t3)',
                  textTransform: 'uppercase',
                  letterSpacing: '.1em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {tile.label}
                </span>
              </div>

              {/* Big value */}
              <div style={{
                fontFamily: 'var(--mono)',
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--t0)',
                letterSpacing: '-.01em',
                lineHeight: 1.1,
                marginBottom: 3,
              }}>
                {tile.value}
              </div>

              {/* Bottom: timestamp / status */}
              <div style={{
                fontFamily: 'var(--mono)',
                fontSize: 9,
                color: freshness === 'live' ? 'var(--green)'
                     : freshness === 'stale' ? 'var(--amber)'
                     : freshness === 'manual' ? 'var(--t3)'
                     : 'var(--red)',
                letterSpacing: '.04em',
              }}>
                {tile.manual
                  ? t('live.manual_label')
                  : tile.ts
                    ? formatAge(tile.ts, lang)
                    : '—'}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}
